import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";

const fieldStyle = {
  width: "100%", height: "44px", padding: "0 14px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box",
  transition: "border-color 0.2s", fontFamily: "inherit",
};

function FieldLabel({ label, required, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" fontWeight="bold" color="text" sx={{
        mb: 0.5, display: "block", textTransform: "uppercase",
        fontSize: 11, letterSpacing: "0.05em",
      }}>
        {label}{required && " *"}
      </MDTypography>
      {children}
    </MDBox>
  );
}

function Section({ title, icon, children }) {
  return (
    <MDBox mb={4}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 32, height: 32, borderRadius: "8px",
          background: "linear-gradient(135deg, #37474f, #455a64)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 16 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 3 }} />
      {children}
    </MDBox>
  );
}

function AdminForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id && id !== "nouveau");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    nom: "", prenom: "", email: "", mot_de_passe: "",
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/users/${id}`).then(({ data }) => {
        setForm({ nom: data.nom || "", prenom: data.prenom || "", email: data.email || "", mot_de_passe: "" });
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim()) {
      setError("Le prénom est obligatoire");
      return;
    }
    if (!isEdit && !form.mot_de_passe) {
      setError("Le mot de passe est obligatoire pour un nouvel administrateur");
      return;
    }
    setError("");
    setSaving(true);

    const cleanForm = { ...form, role: "admin" };
    if (!cleanForm.mot_de_passe) delete cleanForm.mot_de_passe;

    try {
      if (isEdit) {
        await api.put(`/users/${id}`, cleanForm);
      } else {
        await api.post("/users", cleanForm);
      }
      navigate("/admins");
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={6}>

            <MDBox mb={3} display="flex" alignItems="center" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/admins")}
                sx={{ borderRadius: "10px", px: 2, whiteSpace: "nowrap" }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Retour à la liste
              </MDButton>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#344767" }}>
                {isEdit ? "Modifier un administrateur" : "Ajouter un administrateur"}
              </MDTypography>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDBox mx={2} mt={-3} py={2} px={3} sx={{
                background: "linear-gradient(135deg, #37474f 0%, #455a64 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(55,71,79,0.4)",
              }}>
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  {isEdit ? "Modifier l'administrateur" : "Nouvel administrateur"}
                </MDTypography>
              </MDBox>

              <MDBox pt={4} pb={4} px={4}>
                {error && (
                  <MDBox mb={3} p={2}
                    sx={{ background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2" }}>
                    <MDTypography variant="caption" color="error" fontWeight="medium">
                      ⚠️ {error}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox component="form" onSubmit={handleSubmit} autoComplete="off">

                  {/* ── IDENTITÉ ── */}
                  <Section title="Identité" icon="person">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label="Prénom *">
                          <input name="prenom" value={form.prenom} onChange={handleChange}
                            placeholder="Prénom" autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#37474f")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label="Nom">
                          <input name="nom" value={form.nom} onChange={handleChange}
                            placeholder="Nom" autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#37474f")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── ACCÈS ── */}
                  <Section title="Accès" icon="lock">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FieldLabel label="Email *">
                          <input name="email" type="text" value={form.email}
                            onChange={handleChange} placeholder="email@association.fr"
                            autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#37474f")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                      </Grid>
                      <Grid item xs={12}>
                        <FieldLabel label={isEdit ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe *"}>
                          <input name="mot_de_passe" type="password" value={form.mot_de_passe}
                            onChange={handleChange}
                            placeholder={isEdit ? "Laisser vide pour ne pas modifier" : "Minimum 8 caractères"}
                            autoComplete="new-password" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#37474f")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                      </Grid>
                    </Grid>
                    <MDBox mt={1} p={2} sx={{ background: "#eceff1", borderRadius: "10px" }}>
                      <MDTypography variant="caption" sx={{ color: "#37474f" }}>
                        ℹ️ L&apos;administrateur utilisera son email et ce mot de passe pour se connecter.
                      </MDTypography>
                    </MDBox>
                  </Section>

                  {/* ── BOUTONS ── */}
                  <MDBox display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <MDButton variant="outlined" color="secondary"
                      onClick={() => navigate("/admins")}>
                      Annuler
                    </MDButton>
                    <MDButton type="submit" disabled={saving} sx={{
                      px: 4, background: "linear-gradient(135deg, #37474f, #455a64)",
                      color: "#fff", borderRadius: "8px", fontWeight: 700,
                      "&:hover": { background: "#263238" },
                    }}>
                      {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer l'administrateur"}
                    </MDButton>
                  </MDBox>

                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AdminForm;